import classNames from 'classnames';
import React, { useRef, useCallback } from 'react';
import { GetPriorityCallback, usePriorityResize } from 'hooks/usePriorityResize';
import { usePortalResize } from 'hooks/usePortalResize';
import './ActionsBar.less';
import { IActionBarItemData, IActionBarItemClick } from './ActionsBarItem';
import { ActionBarItems } from './ActionsBarItems';

export interface IActionBarProps {
  className?: string;
  actionClassName?: string;
  primaryActionsClassName?: string;
  secondaryActionsClassName?: string;
  actionGroups: IActionBarItemData[];
  onClick: IActionBarItemClick;
}

function getRootClasses(customClassName?: string) {
  return classNames('dpc-action-bar', customClassName);
}

function getActionClasses(customClassName?: string) {
  return classNames('dpc-action-bar__action', customClassName);
}

export function ActionsBar({
  actionGroups,
  onClick,
  className,
  actionClassName,
  primaryActionsClassName,
  secondaryActionsClassName,
}: IActionBarProps) {
  const rootElement = useRef<HTMLDivElement>(null);
  const primaryContainer = useRef<HTMLDivElement>(null);
  const secondaryContainer = useRef<HTMLDivElement>(null);

  const getChildren = useCallback(() => {
    if (primaryContainer.current != null && secondaryContainer.current != null) {
      return [
        ...Array.from(primaryContainer.current.children),
        ...Array.from(secondaryContainer.current.children),
      ] as HTMLElement[];
    }

    return [];
  }, []);
  const getAvailableSpace = useCallback(() => (rootElement.current && rootElement.current.clientWidth) || 0, []);
  const getPriority: GetPriorityCallback = useCallback(
    ({ index }) => {
      return actionGroups[index]?.priority || 0;
    },
    [actionGroups]
  );

  const [visibleItems, updateVisibility] = usePriorityResize(
    actionGroups,
    getAvailableSpace,
    getChildren,
    getPriority,
    'horizontal',
    1
  );

  usePortalResize(updateVisibility);

  const visiblePrimaries = (visibleItems || [])
    .filter(item => item.isPrimary)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  const visibleSecondaries = (visibleItems || [])
    .filter(item => !item.isPrimary)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div ref={rootElement} className={getRootClasses(className)}>
      <div ref={primaryContainer} className={classNames(`dpc-action-bar__primary`, primaryActionsClassName)}>
        <ActionBarItems
          actions={visiblePrimaries}
          onClick={onClick}
          actionClassName={getActionClasses(actionClassName)}
        />
      </div>
      <div ref={secondaryContainer} className={classNames(`dpc-action-bar__secondary`, secondaryActionsClassName)}>
        <ActionBarItems
          actions={visibleSecondaries}
          onClick={onClick}
          actionClassName={getActionClasses(actionClassName)}
        />
      </div>
    </div>
  );
}

// export const ActionsBar = React.memo(_ActionsBar);
